import { PositionType } from 'data/types';
import {
  isPointInPolygon,
  isRectInPolygon,
  polygonBoundingBox
} from 'utils/footprint';
import { estimateSpaceLabelSize } from 'utils/space-label-size';

export { estimateSpaceLabelSize };

export type SpaceLabelSize = {
  width: number;
  height: number;
};

export type SpaceLabelObstacle = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const SAMPLE_STEPS = 16;
export const SPACE_LABEL_OBSTACLE_CLEARANCE = 16;

function distanceToSegment(
  point: PositionType,
  start: PositionType,
  end: PositionType
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq
    )
  );
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
}

function minDistanceToPolygonEdges(
  point: PositionType,
  vertices: PositionType[]
): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < vertices.length; i += 1) {
    const distance = distanceToSegment(
      point,
      vertices[i],
      vertices[(i + 1) % vertices.length]
    );
    if (distance < best) {
      best = distance;
    }
  }
  return best;
}

function labelBoxFromCenter(
  center: PositionType,
  size: SpaceLabelSize
): { x: number; y: number; width: number; height: number } {
  return {
    x: center.x - size.width / 2,
    y: center.y - size.height / 2,
    width: size.width,
    height: size.height
  };
}

function inflateObstacle(
  obstacle: SpaceLabelObstacle,
  pad: number
): SpaceLabelObstacle {
  return {
    x: obstacle.x - pad,
    y: obstacle.y - pad,
    width: obstacle.width + pad * 2,
    height: obstacle.height + pad * 2
  };
}

function boxesOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: SpaceLabelObstacle
): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function overlapArea(
  a: { x: number; y: number; width: number; height: number },
  b: SpaceLabelObstacle
): number {
  const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  if (width <= 0 || height <= 0) return 0;
  return width * height;
}

export function spaceRectVertices(
  width: number,
  height: number
): PositionType[] {
  return [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height }
  ];
}

export function isLabelBoxInsidePolygon(
  center: PositionType,
  size: SpaceLabelSize,
  vertices: PositionType[]
): boolean {
  if (vertices.length < 3) return false;
  if (!isPointInPolygon(center, vertices)) return false;
  return isRectInPolygon(labelBoxFromCenter(center, size), vertices);
}

export function labelBoxOverlapsObstacle(
  center: PositionType,
  size: SpaceLabelSize,
  obstacles: SpaceLabelObstacle[],
  clearance: number = SPACE_LABEL_OBSTACLE_CLEARANCE
): boolean {
  const box = labelBoxFromCenter(center, size);
  return obstacles.some((obstacle) =>
    boxesOverlap(box, inflateObstacle(obstacle, clearance))
  );
}

export function labelBoxObstacleOverlapArea(
  center: PositionType,
  size: SpaceLabelSize,
  obstacles: SpaceLabelObstacle[],
  clearance: number = SPACE_LABEL_OBSTACLE_CLEARANCE
): number {
  const box = labelBoxFromCenter(center, size);
  return obstacles.reduce(
    (total, obstacle) =>
      total + overlapArea(box, inflateObstacle(obstacle, clearance)),
    0
  );
}

function collectObstacleSlotCandidates(
  obstacles: SpaceLabelObstacle[],
  labelSize: SpaceLabelSize,
  preferred: PositionType,
  vertices: PositionType[]
): PositionType[] {
  const pad = SPACE_LABEL_OBSTACLE_CLEARANCE;
  const slots: PositionType[] = [];
  obstacles.forEach((obstacle) => {
    const candidates: PositionType[] = [
      {
        x: preferred.x,
        y: obstacle.y - pad - labelSize.height / 2
      },
      {
        x: preferred.x,
        y: obstacle.y + obstacle.height + pad + labelSize.height / 2
      },
      {
        x: obstacle.x - pad - labelSize.width / 2,
        y: preferred.y
      },
      {
        x: obstacle.x + obstacle.width + pad + labelSize.width / 2,
        y: preferred.y
      }
    ];
    candidates.forEach((slot) => {
      if (isPointInPolygon(slot, vertices)) {
        slots.push(slot);
      }
    });
  });
  return slots;
}

function collectInteriorSamplePoints(vertices: PositionType[]): PositionType[] {
  const bb = polygonBoundingBox(vertices);
  const spanX = bb.maxX - bb.minX;
  const spanY = bb.maxY - bb.minY;
  if (spanX <= 0 || spanY <= 0) return [];

  const samples: PositionType[] = [];
  const bboxCenter = {
    x: (bb.minX + bb.maxX) / 2,
    y: (bb.minY + bb.maxY) / 2
  };
  if (isPointInPolygon(bboxCenter, vertices)) {
    samples.push(bboxCenter);
  }

  for (let row = 0; row <= SAMPLE_STEPS; row += 1) {
    for (let col = 0; col <= SAMPLE_STEPS; col += 1) {
      const point = {
        x: bb.minX + (spanX * col) / SAMPLE_STEPS,
        y: bb.minY + (spanY * row) / SAMPLE_STEPS
      };
      if (isPointInPolygon(point, vertices)) {
        samples.push(point);
      }
    }
  }
  return samples;
}

export function polygonVisualCenter(
  vertices: PositionType[]
): PositionType | null {
  if (vertices.length < 3) return null;

  const samples = collectInteriorSamplePoints(vertices);
  if (samples.length === 0) return null;

  let best = samples[0];
  let bestClearance = minDistanceToPolygonEdges(best, vertices);
  for (let i = 1; i < samples.length; i += 1) {
    const clearance = minDistanceToPolygonEdges(samples[i], vertices);
    if (clearance > bestClearance) {
      best = samples[i];
      bestClearance = clearance;
    }
  }
  return best;
}

function isInsideSpaceLabelAnchor(
  point: PositionType,
  vertices: PositionType[],
  labelSize: SpaceLabelSize,
  obstacles: SpaceLabelObstacle[],
  clearance: number
): boolean {
  return (
    isLabelBoxInsidePolygon(point, labelSize, vertices) &&
    !labelBoxOverlapsObstacle(point, labelSize, obstacles, clearance)
  );
}

export function findInsideSpaceLabelAnchor(
  vertices: PositionType[],
  labelSize: SpaceLabelSize,
  obstacles: SpaceLabelObstacle[] = [],
  clearance: number = SPACE_LABEL_OBSTACLE_CLEARANCE
): PositionType | null {
  const preferred = polygonVisualCenter(vertices);
  if (!preferred) return null;
  if (
    isInsideSpaceLabelAnchor(
      preferred,
      vertices,
      labelSize,
      obstacles,
      clearance
    )
  ) {
    return preferred;
  }

  const samples = [
    ...collectObstacleSlotCandidates(obstacles, labelSize, preferred, vertices),
    ...collectInteriorSamplePoints(vertices)
  ];
  let bestFit: PositionType | null = null;
  let bestFitDistance = Number.POSITIVE_INFINITY;
  samples.forEach((point) => {
    if (
      !isInsideSpaceLabelAnchor(
        point,
        vertices,
        labelSize,
        obstacles,
        clearance
      )
    ) {
      return;
    }
    const distance = Math.hypot(point.x - preferred.x, point.y - preferred.y);
    if (distance < bestFitDistance) {
      bestFit = point;
      bestFitDistance = distance;
    }
  });
  return bestFit;
}

export function getSpaceLabelAnchor(
  vertices: PositionType[],
  labelSize: SpaceLabelSize,
  obstacles: SpaceLabelObstacle[] = []
): PositionType | null {
  const preferred = polygonVisualCenter(vertices);
  if (!preferred) return null;

  const inside = findInsideSpaceLabelAnchor(vertices, labelSize, obstacles);
  if (inside) return inside;

  const samples = [
    ...collectObstacleSlotCandidates(obstacles, labelSize, preferred, vertices),
    ...collectInteriorSamplePoints(vertices)
  ];
  let bestClear: PositionType | null = null;
  let bestClearDistance = Number.POSITIVE_INFINITY;
  let bestMinOverlap = preferred;
  let bestOverlapArea = labelBoxObstacleOverlapArea(
    preferred,
    labelSize,
    obstacles
  );

  samples.forEach((point) => {
    if (!isPointInPolygon(point, vertices)) {
      return;
    }
    const distance = Math.hypot(point.x - preferred.x, point.y - preferred.y);
    if (
      !labelBoxOverlapsObstacle(point, labelSize, obstacles) &&
      distance < bestClearDistance
    ) {
      bestClear = point;
      bestClearDistance = distance;
    }
    const overlap = labelBoxObstacleOverlapArea(point, labelSize, obstacles);
    if (overlap < bestOverlapArea) {
      bestMinOverlap = point;
      bestOverlapArea = overlap;
    }
  });

  return bestClear ?? bestMinOverlap;
}
